import React, { useState, useEffect } from "react";
import { Download } from "lucide-react";
import html2pdf from "html2pdf.js";

const QuotationModal = ({ onClose }) => {
  const [formData, setFormData] = useState({});
  const [cartItems, setCartItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const existingCartItems = JSON.parse(localStorage.getItem("cartItems")) || [];

  useEffect(() => {
    const initialFormData = {
      name: existingCartItems[0]?.name || "",
      mobile: existingCartItems[0]?.mobile || "",
      address: existingCartItems[0]?.address || "",
      pincode: existingCartItems[0]?.pincode || "",
      selectedLocation: existingCartItems[0]?.selectedLocation || "",
    };

    const calculatedTotal = existingCartItems.reduce(
      (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
      0,
    );

    setFormData(initialFormData);
    setCartItems(existingCartItems);
    setTotal(calculatedTotal);
  }, []);

  const quotationNumber = `QT-${Date.now().toString().slice(-6)}`;

  const currentDate = new Date().toLocaleDateString("en-IN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const sendOrderData = async () => {
    const cartItemsDetails = existingCartItems
      .map((item, index) => {
        return `(${index + 1}) Product: ${item.productName}, Pattern: ${
          item.productsPattern || "Standard"
        }, Rate: ₹${item.rate}, Area: ${item.sqft} sq ft, Apply Area: ${
          item.Area
        }, Discount: ${item.discount}%, Effective Rate: ₹${
          item.effectiveRate
        }, Subtotal: ₹${item.amount}, Total: ₹${parseFloat(
          item.amountWithIgst || 0,
        ).toFixed(2)} (incl. taxes)`;
      })
      .join("\n");

    const message = `New order from ${formData.name}. Address: ${
      formData.address
    }, Pincode: ${formData.pincode}, Location: ${
      formData.selectedLocation
    }\n\nOrder Details:\n${cartItemsDetails}\n\nTotal Amount: ₹${total.toFixed(
      2,
    )}`;

    const response = await fetch(
      `https://webadmin.naturx.co.in/api/save_contact?name=${encodeURIComponent(
        formData.name || "-",
      )}&phone=${encodeURIComponent(
        formData.mobile || "-",
      )}&email=${encodeURIComponent("-")}&msg=${encodeURIComponent(message)}`,
    );

    if (!response.ok) {
      throw new Error("Failed to send contact information.");
    }
  };

  const handleDownloadPDF = async () => {
    const element = document.querySelector(".quotation-content");
    if (!element) return;

    try {
      setIsLoading(true);

      // Trigger backend request asynchronously so it never blocks PDF execution
      sendOrderData().catch((err) => console.error("API save failed:", err));

      const clonedElement = element.cloneNode(true);

      // Remove the company header section completely from the PDF clone
      const companyHeader = clonedElement.querySelector(
        ".company-header-section",
      );
      if (companyHeader) {
        companyHeader.remove();
      }

      // Create a temporary container offscreen to render the element reliably
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.backgroundColor = "#ffffff";
      container.appendChild(clonedElement);
      document.body.appendChild(container);

      const opt = {
        margin: [0.3, 0.3, 0.3, 0.3],
        filename: `Quotation-${quotationNumber}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
      };

      await html2pdf().set(opt).from(clonedElement).save();

      document.body.removeChild(container);
    } catch (error) {
      console.error("Error during PDF download:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50">
        <div className="h-full w-full overflow-y-auto p-2 sm:p-4 md:p-6 lg:p-8">
          <div className="min-h-full flex items-center justify-center">
            <div className="relative w-full max-w-7xl mx-auto bg-white rounded-lg shadow-xl">
              {/* Header with Actions - Hidden on Print */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 rounded-t-lg print:hidden z-10">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                    Quotation Preview
                  </h2>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    {/* Download PDF Button */}
                    <button
                      onClick={handleDownloadPDF}
                      disabled={isLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                    >
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <svg
                            className="animate-spin h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          <span>Generating...</span>
                        </span>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          <span>Download PDF</span>
                        </>
                      )}
                    </button>

                    {/* Close Button */}
                    <button
                      onClick={onClose}
                      disabled={isLoading}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none rounded-full p-1 ml-2"
                      aria-label="Close quotation modal"
                    >
                      <svg
                        className="w-5 h-5 sm:w-6 sm:h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Document Content */}
              <div className="quotation-content p-3 sm:p-6 md:p-8 lg:p-12">
                {/* Customer and Quotation Details */}
                <div
                  className="mb-6 sm:mb-8"
                  style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f9fafb" }}>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "12px 16px",
                            textAlign: "left",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1f2937",
                            width: "50%",
                          }}
                        >
                          Customer Details
                        </th>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "12px 16px",
                            textAlign: "left",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1f2937",
                            width: "50%",
                          }}
                        >
                          Quotation Details
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "12px 16px",
                            verticalAlign: "top",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: "1.6",
                            }}
                          >
                            <div>
                              <strong>Name:</strong> {formData.name}
                            </div>
                            <div>
                              <strong>Mobile:</strong> {formData.mobile}
                            </div>
                            <div>
                              <strong>Address:</strong> {formData.address}
                            </div>
                            <div>
                              <strong>Pincode:</strong> {formData.pincode}
                            </div>
                            <div>
                              <strong>Location:</strong>{" "}
                              {formData.selectedLocation}
                            </div>
                          </div>
                        </td>
                        <td
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "12px 16px",
                            verticalAlign: "top",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: "1.6",
                            }}
                          >
                            <div>
                              <strong>Date:</strong> {currentDate}
                            </div>
                            <div>
                              <strong>Valid Until:</strong>{" "}
                              {new Date(
                                Date.now() + 30 * 24 * 60 * 60 * 1000,
                              ).toLocaleDateString("en-IN", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </div>
                            <div>
                              <strong>Payment:</strong> 30 days
                            </div>
                            <div>
                              <strong>Delivery:</strong> 7-10 business days
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Items Table with optimized spacing and forced page flow protection */}
                <div
                  className="mb-6 sm:mb-8"
                  style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#1f2937",
                      marginBottom: "16px",
                      borderBottom: "1px solid #d1d5db",
                      paddingBottom: "4px",
                    }}
                  >
                    Items & Details
                  </h3>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      tableLayout: "fixed",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#f9fafb" }}>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "8px 4px",
                            textAlign: "center",
                            fontSize: "11px",
                            fontWeight: "600",
                            width: "5%",
                          }}
                        >
                          S.No
                        </th>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "8px 4px",
                            textAlign: "left",
                            fontSize: "11px",
                            fontWeight: "600",
                            width: "15%",
                          }}
                        >
                          Product
                        </th>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "8px 4px",
                            textAlign: "left",
                            fontSize: "11px",
                            fontWeight: "600",
                            width: "13%",
                          }}
                        >
                          Pattern
                        </th>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "8px 4px",
                            textAlign: "left",
                            fontSize: "11px",
                            fontWeight: "600",
                            width: "12%",
                          }}
                        >
                          Area
                        </th>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "8px 4px",
                            textAlign: "right",
                            fontSize: "11px",
                            fontWeight: "600",
                            width: "8%",
                          }}
                        >
                          Rate
                        </th>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "8px 4px",
                            textAlign: "right",
                            fontSize: "11px",
                            fontWeight: "600",
                            width: "8%",
                          }}
                        >
                          Sq.Ft
                        </th>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "8px 4px",
                            textAlign: "right",
                            fontSize: "11px",
                            fontWeight: "600",
                            width: "8%",
                          }}
                        >
                          Disc%
                        </th>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "8px 4px",
                            textAlign: "right",
                            fontSize: "11px",
                            fontWeight: "600",
                            width: "11%",
                          }}
                        >
                          Eff.Rate
                        </th>
                        <th
                          style={{
                            border: "1px solid #d1d5db",
                            padding: "8px 4px",
                            textAlign: "right",
                            fontSize: "11px",
                            fontWeight: "600",
                            width: "20%",
                          }}
                        >
                          Amount (₹)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.map((item, index) => (
                        <tr key={index}>
                          <td
                            style={{
                              border: "1px solid #d1d5db",
                              padding: "8px 4px",
                              textAlign: "center",
                              fontSize: "11px",
                              wordBreak: "break-word",
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            style={{
                              border: "1px solid #d1d5db",
                              padding: "8px 4px",
                              fontSize: "11px",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.productName}
                          </td>
                          <td
                            style={{
                              border: "1px solid #d1d5db",
                              padding: "8px 4px",
                              fontSize: "11px",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.productsPattern || "Standard"}
                          </td>
                          <td
                            style={{
                              border: "1px solid #d1d5db",
                              padding: "8px 4px",
                              fontSize: "11px",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.Area}
                          </td>
                          <td
                            style={{
                              border: "1px solid #d1d5db",
                              padding: "8px 4px",
                              textAlign: "right",
                              fontSize: "11px",
                              wordBreak: "break-word",
                            }}
                          >
                            ₹{item.rate}
                          </td>
                          <td
                            style={{
                              border: "1px solid #d1d5db",
                              padding: "8px 4px",
                              textAlign: "right",
                              fontSize: "11px",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.sqft}
                          </td>
                          <td
                            style={{
                              border: "1px solid #d1d5db",
                              padding: "8px 4px",
                              textAlign: "right",
                              fontSize: "11px",
                              wordBreak: "break-word",
                            }}
                          >
                            {item.discount}%
                          </td>
                          <td
                            style={{
                              border: "1px solid #d1d5db",
                              padding: "8px 4px",
                              textAlign: "right",
                              fontSize: "11px",
                              wordBreak: "break-word",
                            }}
                          >
                            ₹{item.effectiveRate}
                          </td>
                          <td
                            style={{
                              border: "1px solid #d1d5db",
                              padding: "8px 4px",
                              textAlign: "right",
                              fontSize: "11px",
                              fontWeight: "500",
                              wordBreak: "break-word",
                              whiteSpace: "nowrap",
                            }}
                          >
                            ₹{parseFloat(item.amountWithIgst || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Total Section */}
                <div
                  className="flex justify-end mb-6 sm:mb-8"
                  style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
                >
                  <div className="w-full sm:w-80">
                    <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs sm:text-sm">
                          <span>Subtotal:</span>
                          <span>
                            ₹
                            {cartItems
                              .reduce(
                                (sum, item) =>
                                  sum + parseFloat(item.amount || 0),
                                0,
                              )
                              .toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs sm:text-sm">
                          <span>IGST (18%):</span>
                          <span>
                            ₹
                            {(
                              total -
                              cartItems.reduce(
                                (sum, item) =>
                                  sum + parseFloat(item.amount || 0),
                                0,
                              )
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className="border-t border-gray-300 pt-2">
                          <div className="flex justify-between text-sm sm:text-lg font-bold">
                            <span>Total Amount:</span>
                            <span>₹{total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 border-b border-gray-300 pb-1">
                    Terms & Conditions
                  </h3>
                  <div className="text-xs sm:text-sm text-gray-700 space-y-1">
                    <p>
                      • This quotation is valid for 30 days from the date of
                      issue.
                    </p>
                    <p>• Payment terms: 30 days from delivery.</p>
                    <p>
                      • Delivery will be made within 7-10 business days after
                      order confirmation.
                    </p>
                    <p>
                      • Installation charges are not included in the quoted
                      price.
                    </p>
                    <p>
                      • Any additional requirements will be charged separately.
                    </p>
                    <p>
                      • This quotation is subject to our standard terms and
                      conditions.
                    </p>
                    <p>
                      <strong>Warranty:</strong> NO PART OF WORK CONTRACT IS
                      WARRANTED
                    </p>
                    <p>
                      <strong>Bank Details:</strong>
                    </p>
                    <p>Kotak Mahindra Bank</p>
                    <p>A/C Name: NATURX COLOUR TEXTURE</p>
                    <p>A/C Number: 6149999755</p>
                    <p>IFSC: KKBK0001986</p>
                    <p>
                      1. Advance once processed will not be refunded under any
                      circumstance.
                    </p>
                    <p>
                      2. Once advance is processed, it implicitly states that
                      you agree to terms and conditions mentioned in this.
                    </p>
                    <p>
                      3. The estimate is based on measurement provided. The
                      final billing will be based on actual area.
                    </p>
                    <p>
                      4. The price includes Material and Labour. Surfaces less
                      than 1 feet in width will be considered 1 feet.
                    </p>
                    <p>
                      5. Project will start on approx. 30 days from the date of
                      advance payment, Color Selections, Signed.
                    </p>
                    <p>
                      6. Once project starts it will take approx. 4 to 6 days to
                      complete the project once started.
                    </p>
                    <p>
                      7. On completion of the project, the remaining balance of
                      25% should be paid within 3 Days. Strictly No Retention
                      allowed. If Not paid 24% interest will be charged per
                      annum.
                    </p>
                    <p>
                      8. The terms and conditions in this document supersede
                      other documents like your Purchase Order, Work Order, etc.
                    </p>
                    <p>
                      9. The Quotation is valid for 30 days. We reserve the
                      right to change the prices or withdraw the quotation until
                      you sign this document and process the advance.
                    </p>
                    <p>
                      10. The entire project has to be executed in a single
                      phase. We will not be able to break the project into
                      multiple phases.
                    </p>
                    <p>
                      11. If the project is delayed from your side, the new
                      schedule has to be worked out mutually. We can only agree
                      to dates when our resources are available.
                    </p>
                    <p>
                      12. Lime Plaster Color Shades vary around 25% from the
                      chosen color due to manual application, compression and
                      distribution of pigments. Lime Plaster is applied by hand
                      so uniform application like Paint is not possible, nor we
                      would try to mimic paint. Variation in texture is natural.
                      It is considered normal and should not be treated as an
                      application defect.
                    </p>
                    <p>
                      13. Our material is very thick, it will not be possible to
                      apply it inside the grooves and small areas. You will have
                      to get it painted after our work is completed.
                    </p>
                    <p>
                      14. Provide plumb walls with 2 Coat Water-based primer
                      applied on it. Erected double metal scaffolding has to be
                      provided by you.
                    </p>
                    <p>
                      15. If our product is applied on Wood or Gypsum Panels,
                      the risk of joint cracking is very high. You will have to
                      apply reinforcement tapes and fill the joints with
                      high-quality adhesives.
                    </p>
                    <p>
                      16. In any case, we do not warranty the cracks. Any
                      re-work arising due to cracks will be charged.
                    </p>
                    <p>
                      17. Please note that this product is very difficult to
                      touch-up if there is any damage after our work is
                      completed. Make sure all other work is completed before
                      Limocoat work starts.
                    </p>
                    <p>
                      18. Lime Plaster is absorbent so care should be taken to
                      cover it with plastic, etc. during any other work nearby.
                      For example, Polishing of marbles, Painting of Ceiling,
                      etc.
                    </p>
                    <p>
                      19. For work scheduling please treat Lime Plaster as Wall
                      Paper / Finish Paint, meaning that it should be the last
                      thing in the schedule, just before installation of
                      fittings, etc. A dust-free site is required.
                    </p>
                    <p>
                      20. Existing electric cables, light fixtures, Air
                      conditioners, and other office furniture have to be
                      removed and installed by you if required.
                    </p>
                    <p>
                      21. You will pay for clearance of Debris from the site.
                    </p>
                    <p>
                      22. You will have to supply free electricity and free
                      water during the execution of the project.
                    </p>
                    <p>
                      23. You will have to provide a secured area at the site to
                      store our material and equipment.
                    </p>
                    <p>
                      24. You will have to take necessary municipal / local
                      bodies permissions and pay for it.
                    </p>
                    <p>
                      25. NaturX Colour Texture is just a supplier of the
                      products.
                    </p>
                    <p>
                      26. We as an applicator are responsible for actual
                      application and the quality of the finishes.
                    </p>
                    <p>
                      <strong>Measurement Options:</strong>
                    </p>
                    <p>
                      1: Joint Measurement: 100% joint measurement Within 3 Days
                      of completion of project. (No Additional Charges)
                    </p>
                    <p>
                      2: Spot Check Measurement: We will do the measurement
                      alone, You can verify a maximum of 10% of the project.
                      Within 3 Days of completion of project. (No Additional
                      Charges)
                    </p>
                    <p>
                      3: Repeat Measurement: We will do 100% measurement and you
                      will verify 100% measurement. Within 7 Days of completion
                      of project. Additional Rs.5/- per sqft charge applicable
                    </p>
                    <p>
                      4: Third Party Measurement: A professional agency will do
                      the measurement. (You will pay 100% of the agency charges)
                    </p>
                    <p>
                      5: Any Other Method: Any other method from your side.
                      Provide details….Provide your method procedure and
                      documentation (Additional Charges may apply)
                    </p>
                    <p>Option Selected ______</p>
                    <p>Customer Signature_________________________</p>
                  </div>
                </div>

                {/* Footer */}
                <div
                  className="border-t border-gray-300 pt-4 sm:pt-6"
                  style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
                    <div className="text-xs sm:text-sm text-gray-600">
                      <p>Thank you for your business!</p>
                      <p>
                        For any queries, please contact us at the above details.
                      </p>
                    </div>
                    <div className="w-full sm:w-auto text-center sm:text-right">
                      <div className="border-t border-gray-400 pt-2 mt-8 sm:mt-12 w-32 sm:w-40 ml-auto">
                        <p className="text-xs sm:text-sm text-gray-600">
                          Authorized Signature
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Actions - Hidden on Print */}
              <div className="px-3 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg print:hidden">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <p className="text-xs sm:text-sm text-gray-600">
                    Total Amount: ₹{total.toFixed(2)}
                  </p>
                  <button
                    onClick={onClose}
                    disabled={isLoading}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuotationModal;
